import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { 
  Shield, 
  Zap, 
  Activity, 
  TrendingUp, 
  AlertCircle, 
  Clock, 
  Settings, 
  MapPin,
  Battery,
  Thermometer,
  Gauge,
  Users,
  DollarSign,
  BarChart3,
  Eye,
  Bell
} from 'lucide-react';

const FleetDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [notifications, setNotifications] = useState(5);
  const [liveData, setLiveData] = useState({
    totalVehicles: 47,
    activeNow: 32,
    fuelEfficiency: 87,
    maintenanceAlerts: 8
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData(prev => ({
        ...prev,
        activeNow: prev.activeNow + Math.floor(Math.random() * 3 - 1),
        fuelEfficiency: Math.max(80, Math.min(95, prev.fuelEfficiency + Math.random() * 2 - 1))
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const vehicleStatusData = [
    { status: 'Operational', count: 32, color: '#10B981', percentage: 68 },
    { status: 'Maintenance', count: 8, color: '#F59E0B', percentage: 17 },
    { status: 'Inactive', count: 5, color: '#6B7280', percentage: 11 },
    { status: 'Critical', count: 2, color: '#EF4444', percentage: 4 }
  ];

  const fuelTrendData = [
    { time: '00:00', consumption: 45, cost: 218, efficiency: 85 },
    { time: '04:00', consumption: 38, cost: 184, efficiency: 88 },
    { time: '08:00', consumption: 72, cost: 349, efficiency: 82 },
    { time: '12:00', consumption: 95, cost: 461, efficiency: 79 },
    { time: '16:00', consumption: 88, cost: 427, efficiency: 81 },
    { time: '20:00', consumption: 65, cost: 315, efficiency: 84 }
  ];

  const performanceMetrics = [
    { name: 'Fleet Utilization', value: 87, target: 90, icon: Gauge },
    { name: 'Fuel Efficiency', value: liveData.fuelEfficiency, target: 85, icon: Zap },
    { name: 'Safety Score', value: 94, target: 95, icon: Shield },
    { name: 'Uptime', value: 92, target: 88, icon: Activity }
  ];

  const criticalVehicles = [
    { id: 'EX-001', type: 'Excavator', location: 'Site Alpha', issue: 'Hydraulic Pressure Low', severity: 'high', hours: '2.3h' },
    { id: 'DT-005', type: 'Dump Truck', location: 'Site Beta', issue: 'Engine Temperature High', severity: 'critical', hours: '0.5h' },
    { id: 'CR-003', type: 'Crane', location: 'Site Gamma', issue: 'Routine Maintenance Due', severity: 'medium', hours: '1d' }
  ];

  const projectOverview = [
    { name: 'Downtown Plaza', vehicles: 12, progress: 78, budget: 2.4, spent: 1.8 },
    { name: 'Highway Extension', vehicles: 18, progress: 45, budget: 5.2, spent: 2.1 },
    { name: 'Residential Complex', vehicles: 9, progress: 92, budget: 1.8, spent: 1.7 },
    { name: 'Industrial Park', vehicles: 8, progress: 23, budget: 3.1, spent: 0.8 }
  ];

  const MetricCard = ({ metric }) => {
    const Icon = metric.icon;
    const percentage = (metric.value / metric.target) * 100;
    const isGood = metric.value >= metric.target;

    return (
      <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6 hover:bg-gray-800/70 transition-all duration-300 group">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-xl ${isGood ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
            <Icon className={`w-6 h-6 ${isGood ? 'text-green-400' : 'text-yellow-400'}`} />
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">{metric.value}%</div>
            <div className="text-xs text-gray-400">Target: {metric.target}%</div>
          </div>
        </div>
        <div className="mb-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-300">{metric.name}</span>
            <span className={`${isGood ? 'text-green-400' : 'text-yellow-400'}`}>
              {percentage.toFixed(0)}%
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ${isGood ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 'bg-gradient-to-r from-yellow-500 to-orange-400'}`}
              style={{ width: `${Math.min(100, percentage)}%` }}
            />
          </div>
        </div>
      </div>
    );
  };

  const StatusPulse = ({ color, size = 'w-3 h-3' }) => (
    <div className={`${size} rounded-full ${color} animate-pulse`} />
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" />
        <div className="absolute top-40 left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      {/* Header */}
      <div className="relative z-10 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Fleet Command
                  </h1>
                  <p className="text-sm text-gray-400">Construction Analytics Suite</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <StatusPulse color="bg-green-400" />
                  <span className="text-gray-300">System Online</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-300">{liveData.activeNow} Active</span>
                </div>
              </div>
              
              <button className="relative p-2 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors">
                <Bell className="w-5 h-5 text-gray-300" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>
              
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Live Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Fleet', value: liveData.totalVehicles, change: '+2', icon: Users },
            { label: 'Active Now', value: liveData.activeNow, change: '+3', icon: Zap },
            { label: 'Efficiency', value: `${liveData.fuelEfficiency}%`, change: '+1.2%', icon: TrendingUp },
            { label: 'Alerts', value: liveData.maintenanceAlerts, change: '-1', icon: AlertCircle }
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-4 hover:scale-105 transition-transform duration-300">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-5 h-5 text-purple-400" />
                  <span className="text-xs text-green-400">{stat.change}</span>
                </div>
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Vehicle Status Overview */}
          <div className="lg:col-span-2 bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Fleet Status Distribution</h2>
              <Eye className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {vehicleStatusData.map((status, index) => (
                <div key={index} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      <span className="text-gray-300 text-sm">{status.status}</span>
                    </div>
                    <span className="text-white font-bold text-lg">{status.count}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-1000"
                      style={{ 
                        width: `${status.percentage}%`,
                        backgroundColor: status.color 
                      }}
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{status.percentage}% of fleet</div>
                </div>
              ))}
            </div>

            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={fuelTrendData}>
                  <defs>
                    <linearGradient id="fuelGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="time" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F3F4F6'
                    }} 
                  />
                  <Area 
                    type="monotone" 
                    dataKey="consumption" 
                    stroke="#8B5CF6" 
                    fillOpacity={1} 
                    fill="url(#fuelGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="space-y-4">
            <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Performance Metrics</h3>
              <div className="space-y-4">
                {performanceMetrics.map((metric, index) => (
                  <MetricCard key={index} metric={metric} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Critical Alerts & Project Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Critical Vehicle Alerts */}
          <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Critical Alerts</h3>
              <div className="flex items-center space-x-2">
                <StatusPulse color="bg-red-400" />
                <span className="text-red-400 text-sm">{criticalVehicles.length} active</span>
              </div>
            </div>
            
            <div className="space-y-3">
              {criticalVehicles.map((vehicle, index) => (
                <div key={index} className="bg-gray-900/50 rounded-xl p-4 border-l-4 border-red-500">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-2 h-2 rounded-full ${vehicle.severity === 'critical' ? 'bg-red-500' : vehicle.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'}`} />
                      <span className="font-medium text-white">{vehicle.id}</span>
                      <span className="text-xs bg-gray-700 px-2 py-1 rounded text-gray-300">{vehicle.type}</span>
                    </div>
                    <span className="text-xs text-gray-400">{vehicle.hours} ago</span>
                  </div>
                  <div className="text-sm text-gray-300 mb-1">{vehicle.issue}</div>
                  <div className="flex items-center text-xs text-gray-400">
                    <MapPin className="w-3 h-3 mr-1" />
                    {vehicle.location}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Project Overview */}
          <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Project Overview</h3>
              <Settings className="w-5 h-5 text-gray-400" />
            </div>
            
            <div className="space-y-4">
              {projectOverview.map((project, index) => (
                <div key={index} className="bg-gray-900/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-medium text-white">{project.name}</div>
                      <div className="text-xs text-gray-400">{project.vehicles} vehicles assigned</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">{project.progress}%</div>
                      <div className="text-xs text-gray-400">Complete</div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-700 rounded-full h-2 mb-3">
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-1000"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Budget: ${project.budget}M</span>
                    <span className="text-gray-400">Spent: ${project.spent}M</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Stats */}
        <div className="bg-gray-800/30 backdrop-blur-lg border border-gray-700/30 rounded-2xl p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-green-400">99.2%</div>
              <div className="text-sm text-gray-400">System Uptime</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">$47,230</div>
              <div className="text-sm text-gray-400">Monthly Savings</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-400">2.3M</div>
              <div className="text-sm text-gray-400">Miles Tracked</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-pink-400">156</div>
              <div className="text-sm text-gray-400">Maintenances Prevented</div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default FleetDashboard;